import FileHelper from "@utils/file-helper";
import {
    ACCEPTED_EXTENSIONS,
    MAX_FILE_SIZE_BYTES,
    MAX_FILE_SIZE_MB,
    MAX_FILES,
    MAX_TOTAL_SIZE_BYTES,
    MAX_TOTAL_SIZE_MB
} from "../constants/convert-document-ai-modal";
import {FileValidationResult, SelectedFileMeta} from "../models/types/convert-document-ai-modal.types";

const ALLOWED_EXTENSIONS = [...ACCEPTED_EXTENSIONS];

const isDuplicate = (file: File, existing: SelectedFileMeta[]): boolean =>
    existing.some(meta => FileHelper.isSameFile(meta, file));

/**
 * Validates a single file against the already-accepted ones.
 * Primitive checks (extension, size) are delegated to FileHelper; the
 * multi-file rules (duplicate, file count and total size) stay local.
 * Returns a rejection reason, or null if the file passes.
 */
const getRejectionReason = (
    file: File,
    acceptedCount: number,
    acceptedMeta: SelectedFileMeta[],
    runningTotal: number
): string | null => {
    if (!FileHelper.isFileExtensionValid(file, ALLOWED_EXTENSIONS)) {
        return "nieobsługiwany format";
    }
    if (!FileHelper.isFileSizeValid(file, MAX_FILE_SIZE_BYTES)) {
        return `przekracza ${MAX_FILE_SIZE_MB} MB`;
    }
    if (isDuplicate(file, acceptedMeta)) {
        return "plik już dodany";
    }
    if (acceptedCount >= MAX_FILES) {
        return `osiągnięto limit ${MAX_FILES} plików`;
    }
    if (runningTotal + file.size > MAX_TOTAL_SIZE_BYTES) {
        return `przekroczono łączny limit ${MAX_TOTAL_SIZE_MB} MB`;
    }
    return null;
};

/**
 * Merges newly selected files into the already-accepted ones, applying the
 * validation rules in order. Pure function — touches neither React state nor DOM.
 */
export const validateIncomingFiles = (
    incoming: File[],
    currentFiles: File[],
    currentMeta: SelectedFileMeta[]
): FileValidationResult => {
    const accepted = [...currentFiles];
    const acceptedMeta = [...currentMeta];
    const rejected: FileValidationResult["rejected"] = [];

    let runningTotal = FileHelper.getTotalSize(currentMeta);

    for (const file of incoming) {
        const reason = getRejectionReason(file, accepted.length, acceptedMeta, runningTotal);
        if (reason) {
            rejected.push({name: file.name, reason});
            continue;
        }
        accepted.push(file);
        acceptedMeta.push({name: file.name, size: file.size});
        runningTotal += file.size;
    }

    return {accepted, acceptedMeta, rejected};
};
